/* The theme control.
 *
 * It lives in the tab bar rather than on a screen because appearance belongs to
 * the app, not to Today or Tasks — and because there is no Settings screen in
 * the React client yet. When Settings arrives this should move into it and the
 * tab bar should go back to being only navigation; a permanent control sitting
 * in the navigation is a compromise, and worth naming as one so it does not
 * quietly become the design.
 *
 * It shows the accent you would get, not the one you have. A swatch of the
 * current theme is a status light; a swatch of the next one is a button, and
 * this is a button.
 */

import { THEMES, useTheme } from './theme';
import type { ThemeName } from './theme';
import { patch } from '../api';

export default function ThemeToggle() {
  const [theme, setDeviceTheme] = useTheme();
  /* Saved to the account as well as the device, so every other signed-in
     device follows — and so signing in again does not bring back the theme
     the account last knew about. Best effort: the device has already changed,
     and a failed save costs only the other devices following. */
  const setTheme = (name: ThemeName) => {
    setDeviceTheme(name);
    void patch('/api/auth/me', { theme: name }).catch(() => null);
  };
  /* The next one round, not merely a different one: with three themes,
     "the first that is not current" would bounce between two for ever. */
  const next = THEMES[(THEMES.findIndex(t => t.name === theme) + 1) % THEMES.length];

  return (
    <button
      type="button"
      className={`theme-toggle theme-swatch-${next.name}`}
      onClick={() => setTheme(next.name)}
      /* The label says what pressing it does. "Theme" alone would leave a
         screen-reader user to press it and find out. */
      aria-label={`Switch to the ${next.label} theme — ${next.note.toLowerCase()}`}
      title={`Switch to ${next.label}`}>
      <span aria-hidden="true" className="theme-dot" />
    </button>
  );
}
